/**
 * The SentinelOne response token (Story 19, task 81): a second, write-scoped
 * Service User token the response layer uses to disconnect and reconnect
 * endpoints. Validated by the API against the Client's console before it
 * is stored; never shown again. The same form is Add and Rotate.
 */

import { useState } from 'react';

import type { ClientTenant } from '@bluesphere/api-client';
import { PillButton } from '@bluesphere/ui-kit';

import { ErrorNote, INPUT, ModalShell } from '@bluesphere/mdr-alert-panel';

export function ResponseTokenModal({
  tenant,
  onCancel,
  onSubmit,
  isPending,
  error,
}: {
  tenant: ClientTenant;
  onCancel: () => void;
  onSubmit: (apiToken: string) => void;
  isPending: boolean;
  error: unknown;
}) {
  const [apiToken, setApiToken] = useState('');
  const canSubmit = apiToken.trim().length > 0 && !isPending;
  const rotating = tenant.sources.sentinelone?.response_token === 'present';

  return (
    <ModalShell onClose={onCancel} width="max-w-lg">
      <div className="border-b border-bluesphere-border px-6 py-5">
        <h2 className="text-lg font-semibold text-bluesphere-textOnLight">
          SentinelOne response token for {tenant.display_name}
        </h2>
        <p className="mt-1 text-sm text-bluesphere-textSecondary">
          A token from a <strong>second</strong> Service User whose role includes the agent
          actions (disconnect from network, reconnect to network) for the site the Client's agents
          are in. It is checked against the console, stored under the Client's own secret path,
          and never shown again. The read-only ingestion token stays as it is.
        </p>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) onSubmit(apiToken.trim());
        }}
        className="space-y-4 px-6 py-5"
        autoComplete="off"
      >
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
            {rotating ? 'New response token' : 'Response token'}
          </span>
          <input
            type="password"
            autoFocus
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
            autoComplete="new-password"
            className={INPUT}
          />
          <span className="mt-1 block text-xs text-bluesphere-muted">
            The check proves the token and the console, not the role: a token from a Service User
            without the agent-actions permission is accepted here and refused by SentinelOne at the
            first live action, which the alert then records as failed.
          </span>
        </label>
        <ErrorNote error={error} />
        <div className="flex items-center justify-end gap-2 pt-2">
          <PillButton variant="ghost" size="sm" type="button" onClick={onCancel}>
            Cancel
          </PillButton>
          <PillButton size="sm" type="submit" disabled={!canSubmit}>
            {isPending ? 'Validating…' : rotating ? 'Validate and replace' : 'Validate and save'}
          </PillButton>
        </div>
      </form>
    </ModalShell>
  );
}
